import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import type { ActionButton } from '@app/src/components/ActionButtons'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewExport from '@app/src/components/Drawer/Views/DrawerViewExport'
import { useDrawer_DEPRECATED } from '@app/src/components/Drawer_DEPRECATED'
import EmptyState from '@app/src/components/EmptyState'
import HasWriteAccess from '@app/src/components/HasWriteAccess'
import LinkButton from '@app/src/components/LinkButton'
import UpdateProvidersDrawer from '@app/src/components/ManageProviderDrawer/UpdateProvidersDrawer'
import Table from '@app/src/components/Table'
import { useRiskSettings } from '@app/src/context/RiskSettingsContext'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useSort from '@app/src/hooks/sorting'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import { CONFIGURATION_PAGE_IDS } from '@app/src/pages/Configurations/ConfigurationsScene'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { ResourceCollectionSceneProps } from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import ResourceCollectionScene from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import type { Provider, RiskTableView } from '@app/src/types/organizations'
import { RiskContext } from '@app/src/types/resourceExplorer'
import { insertIf } from '@app/src/utils/helpersTs'
import { ResourceTypes } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { LabelOutlined, TuneOutlined } from '@mui/icons-material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { generatePath } from 'react-router'
import RiskScreeningIndustryFilters from '../../Filters/RiskScreeningIndustryFilters'
import RiskScreeningIndustryHeader from './RiskScreeningIndustryHeader'
import RiskScreeningIndustryRow from './RiskScreeningIndustryRow'
import { RiskScreeningViews } from './RiskScreeningScene'

interface RiskScreeningIndustrySceneProps {
  tabs?: ResourceCollectionSceneProps['tabs']
  actionButtons?: ResourceCollectionSceneProps['actionButtons']
  subtitle?: React.ReactNode
}

const allowedFilters: string[] = [
  'id',
  'name',
  'subIndustry.id',
  'createdAt',
  'providerOwners.userId',
  'providerOwners.user',
  'websiteAddress',
  'vatNumber',
  'registrationNumber',
  'linkStatus',
  'categoryOptions.id',
  'activityStatus',
  'finalRiskRating',
  'priority',
  'providerApprovalStatus',
  'supplierUsage',
  'tier',
  'organization.id',
  'mappingNodes.actorType.id',
  'mappingNodes.tier',
  'riskStatus',
  'linked',
  'spends.periodName',
  'spends.spendClassification',
  'assessments.periodName',
  'assessments.totalLevel',
  'assessments.section1Level',
  'assessments.section2Level',
  'assessments.section3Level',
  'assessments.section4Level',
  'assessments.section5Level',
  'assessments.assessmentTemplate.name',
  'assessments.assessmentTemplate.id',
  'assessments.assessmentTemplate.AssessmentType',
]

const RiskScreeningIndustryScene: React.FC<RiskScreeningIndustrySceneProps> = ({
  tabs,
  actionButtons,
  subtitle,
}): JSX.Element => {
  const filters = useGetApiQueryFilters(allowedFilters)
  const [pageNumber, itemsPerPage, setPage, setPageSize] = usePaginationWithQuery(undefined, [filters])
  const { sorting, toggleSorting } = useSort()
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()
  const { activeIndustryRiskSettings, isLoadingRiskSettings } = useRiskSettings()
  const hasWriteAccess = useWriteAccess()

  const [selectedProviders, setSelectedProviders] = useState<Array<number>>([])
  const [isUpdateDrawerOpen, openUpdateDrawer, closeUpdateDrawer] = useDrawer_DEPRECATED(false)

  const industryContextFilter = {
    name: 'riskContext',
    filters: [{ value: [RiskContext.Industry], operator: Operators.In }],
  }

  const payload = {
    filter: [...filters, industryContextFilter],
    pagination: {
      pageNumber,
      itemsPerPage,
    },
    sort: sorting,
    include: ['providerOwners.user', 'enrichments.subIndustry'],
  }

  const rawExportPayload = {
    filter: [industryContextFilter],
    pagination: {
      pageNumber,
      itemsPerPage,
    },
    sort: sorting,
    include: ['providerOwners.user'],
  }

  const { items, count, isLoading, isFetching, isError } = useFetchCollectionWithPost<RiskTableView>({
    key: FetchKey.RiskScreening,
    endpoint: endpoints.riskScreening,
    payload,
    options: {
      enabled: Boolean(activeIndustryRiskSettings.length),
    },
  })

  const isFetchingProviders = isLoading || isFetching

  const handleExport = (selectedIds?: number[]) => {
    openDrawer(
      <DrawerViewExport
        resourceType={ResourceTypes.RiskScreening}
        resourceView={RiskScreeningViews.Industry}
        count={count}
        userFilter={filters}
        rawExportPayload={rawExportPayload}
        disableExportSettings
        selectedIds={selectedIds}
      />,
    )
  }

  const buttons: ActionButton[] = [
    ...(actionButtons ?? []),
    {
      label: formatMessage({ id: 'resourceCollections.general.export' }),
      variant: 'outlined',
      onClick: () => handleExport(selectedProviders),
      disabled: isLoading || count === 0,
    },
  ]

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, provider: Provider) => {
    if (e.target.checked) {
      setSelectedProviders(prev => [...prev, provider.id])
    } else {
      setSelectedProviders(prev => prev.filter(providerId => providerId !== provider.id))
    }
  }

  return (
    <ResourceCollectionScene
      title={formatMessage({ id: 'navbar.riskScreening' })}
      actionButtons={buttons}
      tabs={tabs}
      subtitle={subtitle}
      filter={<RiskScreeningIndustryFilters allowedFilters={allowedFilters} implicitFilters={[]} />}
      buttonRow={
        selectedProviders.length
          ? [
              {
                label: formatMessage({ id: 'riskScreening.categorize' }, { count: selectedProviders.length }),
                startIcon: <LabelOutlined />,
                onClick: openUpdateDrawer,
                disabled: isFetchingProviders,
              },
              ...insertIf<ActionButton>(hasWriteAccess, {
                label: formatMessage(
                  { id: 'resourceCollections.general.exportWithCount' },
                  { count: selectedProviders.length },
                ),
                onClick: () => handleExport(selectedProviders),
                disabled: isLoading || count === 0,
              }),
            ]
          : undefined
      }
    >
      <Table<RiskTableView>
        RowComponent={({ row }): JSX.Element => (
          <RiskScreeningIndustryRow
            row={row}
            enabledRiskTypes={activeIndustryRiskSettings}
            onCheckboxChange={handleCheckboxChange}
            selectedProviders={selectedProviders}
            activeFilters={filters}
          />
        )}
        HeaderComponent={() => (
          <RiskScreeningIndustryHeader
            toggleSorting={toggleSorting}
            activeSorting={sorting}
            enabledRiskTypes={activeIndustryRiskSettings}
            selectedProviders={selectedProviders}
            setSelectedProviders={setSelectedProviders}
            providersInPage={items.map(i => i.parentObject)}
          />
        )}
        data={items}
        page={pageNumber}
        pageSize={itemsPerPage}
        setPage={setPage}
        setPageSize={setPageSize}
        isLoading={isLoading || isFetching || isLoadingRiskSettings}
        count={count}
        isError={isError}
        emptyState={
          !activeIndustryRiskSettings.length && (
            <EmptyState
              iconComponent={TuneOutlined}
              title={formatMessage({ id: 'dashboard.sourcing.countryRiskMap.emptyState.title' })}
              description={formatMessage({ id: 'dashboard.sourcing.countryRiskMap.emptyState.description' })}
            >
              <HasWriteAccess>
                <LinkButton
                  variant="outlined"
                  to={generatePath(paths.configurations, { configurationsPage: CONFIGURATION_PAGE_IDS.RiskScreening })}
                >
                  {formatMessage({ id: 'dashboard.sourcing.countryRiskMap.emptyState.action' })}
                </LinkButton>
              </HasWriteAccess>
            </EmptyState>
          )
        }
      />
      <UpdateProvidersDrawer
        selectedProviders={items
          .filter(riskView => selectedProviders.includes(riskView.parentObject.id))
          .map(riskView => riskView.parentObject)
          .map(provider => ({
            providerId: provider.id,
            name: provider.name,
            activityStatus: provider.activityStatus,
            finalRiskRating: provider.finalRiskRating,
            priority: provider.priority,
            providerApprovalStatus: provider.providerApprovalStatus,
            categoryOptionIds: provider.categoryOptions?.map(catOpt => catOpt.id),
            ownerUserIds: provider.ownerUserIds,
            tier: provider.tier,
            supplierUsage: provider.supplierUsage,
          }))}
        setSelectedProviders={setSelectedProviders}
        isDrawerOpen={isUpdateDrawerOpen}
        closeDrawer={closeUpdateDrawer}
      />
    </ResourceCollectionScene>
  )
}

export default RiskScreeningIndustryScene
